import * as React from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AlertTriangleIcon, ExternalLinkIcon, GraduationCapIcon, LogInIcon, RefreshCwIcon } from 'lucide-react';

import { defaultPathForRole, useSession, type UserRole } from '@/components/auth/user-provider';
import { useAuth } from '@/components/auth/auth-provider';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Header } from '@/components/layout/header';
import { Sidebar } from '@/components/layout/sidebar';
import { ProfileMenu } from '@/components/layout/profile-menu';
import { ThemeToggle } from '@/components/theme/theme-toggle';
import { Skeleton } from '@/components/ui/skeleton';

const SIDEBAR_STORAGE_KEY = 'cdf-sidebar-collapsed';
const RETURN_TO_KEY = 'auth:returnTo';

function SessionLoadingScreen() {
  return (
    <div className="bg-muted/40 flex min-h-svh items-center justify-center p-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-4">
        <Skeleton className="size-12 rounded-2xl" />
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-56" />
        <span className="sr-only">Checking sign-in status…</span>
      </div>
    </div>
  );
}

/** Prompt unauthenticated users to sign in, preserving the route they wanted. */
function SignInRequiredScreen() {
  const { signIn } = useSession();
  const { authError, popupBlocked, clearAuthError } = useAuth();
  const location = useLocation();

  const handleSignIn = () => {
    const returnTo = `${location.pathname}${location.search}${location.hash}` || '/dashboard';
    sessionStorage.setItem(RETURN_TO_KEY, returnTo.startsWith('/') ? returnTo : '/dashboard');
    clearAuthError();
    signIn();
  };

  return (
    <div className="bg-background flex h-svh flex-col">
      {/* Top bar with branding + login button in the top-right */}
      <header className="border-border/80 bg-background/90 supports-[backdrop-filter]:bg-background/75 sticky top-0 z-40 flex h-16 shrink-0 items-center border-b backdrop-blur">
        <div className="flex w-full items-center justify-between gap-4 px-4 md:px-6">
          <Link to="/" className="flex items-center gap-3">
            <span className="bg-primary text-primary-foreground ring-primary/20 flex size-9 items-center justify-center rounded-xl shadow-sm ring-4">
              <GraduationCapIcon className="size-5" aria-hidden />
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-sm font-bold tracking-tight">School Attendance</span>
              <span className="text-muted-foreground mt-1 text-[10px] font-semibold tracking-[0.16em] uppercase">
                Operations Console
              </span>
            </span>
          </Link>
          <Button size="sm" onClick={handleSignIn}>
            <LogInIcon />
            Sign in
          </Button>
        </div>
      </header>

      {/* Centered sign-in card */}
      <div className="bg-muted/40 flex flex-1 items-center justify-center p-4">
        <Card className="border-primary/15 w-full max-w-sm shadow-xl shadow-primary/10">
          <CardHeader className="items-center text-center">
            <span className="bg-primary text-primary-foreground ring-primary/20 mx-auto flex size-12 items-center justify-center rounded-2xl shadow-md ring-4">
              <GraduationCapIcon className="size-6" aria-hidden />
            </span>
            <CardTitle>Sign in required</CardTitle>
            <CardDescription>
              School Attendance is protected. Sign in with your Microsoft account to continue.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {authError && (
              <div
                role="alert"
                className="border-destructive/30 bg-destructive/5 text-foreground flex items-start gap-2 rounded-md border p-3 text-sm"
              >
                <AlertTriangleIcon className="text-destructive mt-0.5 size-4 shrink-0" aria-hidden />
                <span>{authError}</span>
              </div>
            )}
            {popupBlocked && (
              <div
                role="alert"
                className="border-border bg-muted/50 flex flex-col gap-3 rounded-md border p-3 text-sm"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
                  <span>
                    Sign-in popup was blocked by your browser. Open this page in a new tab to sign in, or retry below.
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" asChild>
                    <a href={window.location.href} target="_blank" rel="noopener noreferrer">
                      <ExternalLinkIcon /> Open in new tab
                    </a>
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleSignIn}>
                    <RefreshCwIcon /> Retry
                  </Button>
                </div>
              </div>
            )}
            <Button className="w-full" onClick={handleSignIn}>
              <LogInIcon /> {popupBlocked ? 'Retry sign-in' : authError ? 'Try again' : 'Sign in with Microsoft'}
            </Button>
            <Button variant="ghost" className="w-full" asChild>
              <Link to="/">Back to homepage</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function usesStaffChrome(role: UserRole, isActive: boolean): boolean {
  return isActive && (role === 'admin' || role === 'teacher');
}

/**
 * Minimal chrome for pending / family / blocked accounts — branding + theme +
 * profile, no staff sidebar or operational top nav.
 */
function SimpleSignedInShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-background flex h-svh flex-col">
      <header className="border-border/80 bg-background/95 supports-backdrop-filter:bg-background/80 sticky top-0 z-40 border-b backdrop-blur">
        <div className="flex h-14 items-center justify-between gap-3 px-4 md:px-6">
          <Link to="/" className="flex items-center gap-3">
            <span className="bg-primary text-primary-foreground ring-primary/20 flex size-9 items-center justify-center rounded-xl shadow-sm ring-4">
              <GraduationCapIcon className="size-5" aria-hidden />
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-sm font-bold tracking-tight">School Attendance</span>
              <span className="text-muted-foreground mt-1 text-[10px] font-semibold tracking-[0.16em] uppercase">
                Signed in
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <ProfileMenu />
          </div>
        </div>
      </header>
      <main id="main-content" tabIndex={-1} className="bg-muted/35 flex min-h-0 flex-1 flex-col outline-none">
        {children}
      </main>
    </div>
  );
}

export function AppShell() {
  const { session } = useSession();
  const location = useLocation();
  const [collapsed, setCollapsed] = React.useState(
    () => localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);

  const toggleSidebar = () => {
    setCollapsed((value) => {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(!value));
      return !value;
    });
  };

  if (session.status === 'loading') {
    return <SessionLoadingScreen />;
  }

  if (session.status !== 'signedIn') {
    return <SignInRequiredScreen />;
  }

  const { user } = session;

  // Deactivated accounts — force blocked screen.
  if (!user.isActive) {
    if (location.pathname !== '/access-blocked') {
      return <Navigate to="/access-blocked" replace />;
    }
    return (
      <SimpleSignedInShell>
        <Outlet />
      </SimpleSignedInShell>
    );
  }

  // Pending — awaiting-access only.
  if (user.role === 'pending') {
    if (location.pathname !== '/awaiting-access') {
      return <Navigate to="/awaiting-access" replace />;
    }
    return (
      <SimpleSignedInShell>
        <Outlet />
      </SimpleSignedInShell>
    );
  }

  // Parent / student — account landing only (no staff chrome).
  if (user.role === 'parent' || user.role === 'student') {
    if (location.pathname !== '/account') {
      return <Navigate to="/account" replace />;
    }
    return (
      <SimpleSignedInShell>
        <Outlet />
      </SimpleSignedInShell>
    );
  }

  // Staff who land on role-only paths → their home.
  if (
    usesStaffChrome(user.role, user.isActive) &&
    (location.pathname === '/awaiting-access' ||
      location.pathname === '/access-blocked' ||
      location.pathname === '/account')
  ) {
    return <Navigate to={defaultPathForRole(user.role, user.isActive)} replace />;
  }

  return (
    <div className="bg-background flex h-svh flex-col">
      <a
        href="#main-content"
        className="bg-card text-foreground ring-ring sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:rounded-lg focus:px-3 focus:py-2 focus:text-sm focus:ring-2 focus:shadow-lg"
      >
        Skip to main content
      </a>
      <Header onMobileSidebarOpen={() => setMobileSidebarOpen(true)} />
      <div className="flex min-h-0 flex-1">
        <Sidebar
          collapsed={collapsed}
          onToggle={toggleSidebar}
          mobileOpen={mobileSidebarOpen}
          onMobileClose={() => setMobileSidebarOpen(false)}
        />
        <main
          id="main-content"
          tabIndex={-1}
          className="bg-muted/35 flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto p-4 outline-none overscroll-contain md:p-6"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
